import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Mail, Lock, ArrowRight } from "lucide-react";
import { useCustomerAuth } from "@/contexts/CustomerAuthContext";
import SEO from "@/components/storefront/SEO";

export default function AccountLogin() {
  const nav = useNavigate();
  const loc = useLocation();
  const { signIn } = useCustomerAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    const res = await signIn({ email, password });
    setBusy(false);
    if (!res.ok) {
      setError(res.error);
      return;
    }
    const redirect = new URLSearchParams(loc.search).get("redirect") || "/account";
    nav(redirect, { replace: true });
  };

  return (
    <div className="mx-auto grid min-h-[70vh] max-w-md px-6 py-14" data-testid="account-login-page">
      <SEO title="Sign in" description="Sign in to your Luna. Creative Home account." noindex />
      <div className="m-auto w-full">
        <div className="mb-2 text-xs uppercase tracking-[0.3em] text-[#2D2823]">Account</div>
        <h1 className="font-display text-5xl leading-tight md:text-[64px]">Welcome <span className="font-display-soft">back</span></h1>
        <p className="mt-3 text-sm text-[#2D2823]">Sign in to view your orders, addresses and saved items.</p>

        <form onSubmit={submit} className="mt-10 space-y-4">
          <Field icon={Mail} label="Email" type="email" autoComplete="email" value={email} onChange={setEmail} testId="login-email" />
          <Field icon={Lock} label="Password" type="password" autoComplete="current-password" value={password} onChange={setPassword} testId="login-password" />

          {error && <div className="rounded-md bg-rose-50 p-3 text-xs text-rose-700" data-testid="login-error">{error}</div>}

          <button
            type="submit"
            disabled={busy}
            className="flex w-full items-center justify-center gap-2 rounded-full bg-[#1A1A1A] px-6 py-3.5 text-sm text-white transition-colors hover:bg-[#7D8471] disabled:opacity-60"
            data-testid="login-submit"
          >
            {busy ? "Signing in…" : (<>Sign in <ArrowRight size={14} /></>)}
          </button>

          <div className="flex items-center justify-between text-xs text-[#2D2823]">
            <Link to="/account/forgot" className="underline decoration-[#7D8471] underline-offset-4 hover:text-[#1A1A1A]" data-testid="login-forgot-link">
              Forgot your password?
            </Link>
            <Link to="/account/register" className="underline decoration-[#7D8471] underline-offset-4 hover:text-[#1A1A1A]" data-testid="login-register-link">
              Create an account
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ icon: Icon, label, type, value, onChange, testId, autoComplete }) {
  return (
    <label className="block">
      <span className="mb-1 block text-[10px] uppercase tracking-[0.2em] text-[#2D2823]">{label}</span>
      <span className="relative block">
        <Icon size={14} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-[#2D2823]" />
        <input
          type={type}
          autoComplete={autoComplete}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          required
          className="w-full rounded-full border border-[#DDD6C5] bg-white py-3 pl-11 pr-4 text-sm outline-none focus:border-[#1A1A1A]"
          data-testid={testId}
        />
      </span>
    </label>
  );
}
